import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

/**
 * StatCard — THE standard metric tile for the Pro web app.
 *
 * Any "big number + label" summary (KPIs, vote breakdowns, record counts)
 * renders as StatCards inside a StatCardRow — never a hand-rolled
 * text-2xl/font-bold div. Variants carry meaning: success (positive/yes),
 * danger (negative/no), warning (attention), info (totals/derived),
 * default (neutral). Pass onClick to make a tile a drill-down.
 *
 * Live example: /design-system → Metrics. Reference adopter: the legislator
 * Voting History summary. Rules: Documentation/FRONTEND_DESIGN_CONVENTIONS.md.
 */
interface StatCardProps {
  label: string;
  value: number | string;
  variant?: 'default' | 'success' | 'danger' | 'warning' | 'info';
  className?: string;
  onClick?: () => void;
  clickable?: boolean;
  /** Small icon rendered centered above the value, tinted by the variant */
  icon?: React.ElementType;
  /** Secondary line under the label — deltas, context ("12 active", "per job") */
  sublabel?: React.ReactNode;
  /** Pulse skeleton in place of the value while data loads */
  loading?: boolean;
  /** Render the tile as a react-router Link (cmd-clickable). Mutually exclusive with onClick. */
  to?: string;
  /**
   * Marks the tile as the active drill-down (a filter this tile controls is
   * currently applied). Selection is a state the component owns — call sites
   * must not paint their own ring/border for it.
   */
  selected?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  variant = 'default',
  className = '',
  onClick,
  clickable = false,
  icon: Icon,
  sublabel,
  loading = false,
  to,
  selected = false
}) => {
  const getVariantClasses = () => {
    const baseClasses = (() => {
      switch (variant) {
        case 'success':
          return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
        case 'danger':
          return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
        case 'warning':
          return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
        case 'info':
          return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
        default:
          return 'bg-white dark:bg-background-dark-secondary border-border-light dark:border-border-dark-hairline';
      }
    })();

    const hoverClasses = (clickable || onClick || to) ?
      'hover:shadow-lg hover:border-opacity-80 hover:scale-[1.02] transition-all duration-200' : '';
    return `${baseClasses} ${hoverClasses}`;
  };

  const getValueClasses = () => {
    switch (variant) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'info':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-neutral-900 dark:text-white';
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      // A drill-down tile owns its click — don't also trigger a clickable
      // ancestor (tiles nested in list cards, e.g. the lobbying tabs).
      e.stopPropagation();
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const isInteractive = clickable || onClick || to;

  const tileClasses = `block p-4 rounded-lg border group ${getVariantClasses()} ${className} ${
    isInteractive ? 'cursor-pointer' : ''
  } ${selected ? 'ring-2 ring-primary-500' : ''}`;

  const body = (
    <div className="text-center relative">
        {Icon && (
          <Icon className={`w-5 h-5 mx-auto mb-1 ${getValueClasses()}`} aria-hidden="true" />
        )}
        {loading ? (
          <div className="h-8 w-16 mx-auto rounded bg-neutral-200 dark:bg-background-dark-tertiary animate-pulse" />
        ) : (
          <div className={`text-2xl font-bold ${getValueClasses()}`}>
            {value}
          </div>
        )}
        <div className="flex items-center justify-center gap-1 text-sm text-neutral-600 dark:text-text-dark-tertiary mt-1">
          <span>{label}</span>
          {isInteractive && (
            <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
        {sublabel && (
          <div className="text-xs text-neutral-500 dark:text-text-dark-tertiary mt-0.5">
            {sublabel}
          </div>
        )}
      </div>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={tileClasses}
        onClick={(e) => e.stopPropagation()}
        aria-label={`View ${label.toLowerCase()}`}
      >
        {body}
      </Link>
    );
  }

  return (
    <div
      className={tileClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `View ${label.toLowerCase()}` : undefined}
    >
      {body}
    </div>
  );
};

/**
 * StatCardRow — the standard container for a strip of StatCards.
 *
 * Auto-fitting responsive grid: tiles wrap to as many columns as fit, so the
 * same markup works for 3 or 7 metrics without per-page breakpoint tuning.
 * Override minTileWidth only when values are unusually wide (currency).
 */
export const StatCardRow: React.FC<{
  children: React.ReactNode;
  className?: string;
  /** Minimum tile width before wrapping (CSS length, default 9rem) */
  minTileWidth?: string;
}> = ({ children, className = '', minTileWidth = '9rem' }) => (
  <div
    className={`grid gap-4 ${className}`}
    style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minTileWidth}, 1fr))` }}
  >
    {children}
  </div>
);

export default StatCard;
